import React, {useEffect, useMemo, useRef, useState} from 'react'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {
    SimpleGrid,
    Stack,
    List,
    ListItem,
    Box,
    Flex,
    Text,
    Spinner,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useAddressFields from '../../components/forms/useAddressFields'
import Field from '../../components/field'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {MESSAGE_PROPTYPE} from '@salesforce/retail-react-app/app/utils/locale'
import {useAddressSuggestion} from '../../integrations/melissa/hooks/use-melissa'
import debounce from 'lodash/debounce'
import {useOutsideClick} from '@chakra-ui/react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {SITE_COUNTRY_MAP} from '../../constants'
import {AddIcon, ChevronDownType2} from '../icons'

import {sendClickEvent} from '../../utils/gtm-utils'

// MRT multi site
import {getSiteEnvVarValue} from '../../utils/site-config'

const defaultFormTitleAriaLabel = defineMessage({
    defaultMessage: 'Address Form',
    id: 'use_address_fields.label.address_form'
})

const addApartmentLabel = defineMessage({
    defaultMessage: 'Add apartment, office, etc.',
    id: 'use_address_fields.label.add_apartment'
})

const AddressFields = ({
    form,
    prefix = '',
    formTitleAriaLabel = defaultFormTitleAriaLabel,
    isBillingAddress = false
}) => {
    const {data: customer} = useCurrentCustomer()
    const testIdPrefix = isBillingAddress ? 'checkout-billing' : 'checkout-shipping'
    const fields = useAddressFields({form, prefix, testIdPrefix})
    const intl = useIntl()
    const addressFormRef = useRef()
    const styles = useMultiStyleConfig('ShippingAddress')
    const boxRef = useRef()
    const suggestionsListRef = useRef()
    const itemsRef = useRef([])

    // State for address1 input and suggestions
    const [address1, setAddress1] = useState('')
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [showApartment, setShowApartment] = useState(false)
    const [hasFetched, setHasFetched] = useState(false)
    const [expandedGroups, setExpandedGroups] = useState({})

    // Auto-show apartment field when the form value is set externally (e.g. address validation)
    const apartmentValue = form.watch(fields.apartment?.name)
    useEffect(() => {
        if (apartmentValue && !showApartment) {
            setShowApartment(true)
        }
    }, [apartmentValue])

    useOutsideClick({
        ref: boxRef,
        handler: () => setShowSuggestions(false)
    })

    // Initialize the useAddressSuggestion hook
    const {site} = useMultiSite()
    const formCountryCode = form.watch('countryCode')
    const countryCode = formCountryCode || SITE_COUNTRY_MAP[site?.id] || 'US'
    const {isLoading, suggestions, fetchSuggestions} = useAddressSuggestion(address1, countryCode)

    const config = getConfig()
    const siteId = site?.id
    const ADDRESS_SUGGESTIONS_ENABLED = getSiteEnvVarValue(config.app, 'ADDRESS_SUGGESTIONS_ENABLED', siteId) === 'true'
    const ADDRESS_SERVICES_DEBOUNCE = getSiteEnvVarValue(config.app, 'ADDRESS_SERVICES_DEBOUNCE_INTERVAL', siteId)
    const ADDRESS_SERVICES_DEBOUNCE_INTERVAL = ADDRESS_SERVICES_DEBOUNCE ? Number(ADDRESS_SERVICES_DEBOUNCE) : 300
    const ADDRESS_SERVICES_MIN_CHARS = getSiteEnvVarValue(config.app, 'ADDRESS_SERVICES_MIN_CHARS', siteId)
    const MIN_SUGGESTION_CHARS = ADDRESS_SERVICES_MIN_CHARS ? Number(ADDRESS_SERVICES_MIN_CHARS) : 7
    const UNITS_PER_GROUP = 60
    const ADDRESS_SUGGESTION_LIMIT = 4

    // Group suggestions by base address for unit-level display
    const groupedSuggestions = useMemo(() => {
        if (!suggestions || suggestions.length === 0) return []

        // Normalize for grouping/dedupe so accents/case variations of the same
        // value (e.g. "Montréal" vs "Montreal") are treated as equivalent.
        const normalize = (value) =>
            (value || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .trim()
                .toLowerCase()

        // Melissa may collapse all units for a base address into a single
        // suggestion whose address2 is a comma-separated list (e.g.
        // "Ste R17,Ste R36,..."). Expand those into one suggestion per unit
        // so the grouping logic below can present them individually.
        const expandedSuggestions = suggestions.flatMap((s) => {
            if (!s.address2 || !s.address2.includes(',')) return [s]
            const units = s.address2
                .split(',')
                .map((u) => u.trim())
                .filter(Boolean)
            if (units.length <= 1) return [s]
            return units.map((unit) => ({...s, address2: unit}))
        })

        // Standalone addresses (no unit) are always shown as individual items.
        // Only unit-level suggestions (with address2) are grouped together.
        const standalones = expandedSuggestions.filter((s) => !s.address2)
        const withUnits = expandedSuggestions.filter((s) => !!s.address2)

        // Group unit-level suggestions by normalized base address
        const groups = {}
        withUnits.forEach((s) => {
            const key = `${normalize(s.address1)}|${normalize(s.city)}|${normalize(s.postalCode)}`
            if (!groups[key]) {
                groups[key] = {
                    key,
                    address1: s.address1,
                    city: s.city,
                    postalCode: s.postalCode,
                    items: []
                }
            }
            // Dedupe units inside the same group by normalized address2
            const unitKey = normalize(s.address2)
            if (!groups[key].items.some((existing) => normalize(existing.address2) === unitKey)) {
                groups[key].items.push(s)
            }
        })

        // Dedupe standalones against each other and against any group header
        // (a standalone that matches a group's base address is already
        // represented by the group, so drop it).
        const seenStandaloneKeys = new Set()
        const dedupedStandalones = []
        standalones.forEach((s) => {
            const key = `${normalize(s.address1)}|${normalize(s.city)}|${normalize(s.postalCode)}`
            if (seenStandaloneKeys.has(key)) return
            if (groups[key]) return
            seenStandaloneKeys.add(key)
            dedupedStandalones.push(s)
        })

        // Build display items: standalones first, then grouped unit entries
        const result = dedupedStandalones.map((s) => ({type: 'single', suggestion: s}))

        Object.values(groups).forEach((group) => {
            // Sort items by address2 ascending (natural numeric order)
            const sortedItems = [...group.items].sort((a, b) =>
                (a.address2 || '').localeCompare(b.address2 || '', undefined, {numeric: true, sensitivity: 'base'})
            )

            if (sortedItems.length === 1) {
                result.push({type: 'single', suggestion: sortedItems[0]})
            } else {
                // Split into sub-groups of UNITS_PER_GROUP
                for (let i = 0; i < sortedItems.length; i += UNITS_PER_GROUP) {
                    const chunk = sortedItems.slice(i, i + UNITS_PER_GROUP)
                    const start = i + 1
                    const end = i + chunk.length
                    const groupKey = `${group.address1}|${start}-${end}`
                    result.push({
                        type: 'group',
                        key: groupKey,
                        label: [group.address1, group.city, group.postalCode].filter(Boolean).join(', '),
                        rangeLabel: `${start}-${end}`,
                        items: chunk
                    })
                }
            }
        })

        return result
    }, [suggestions])

    // Reset expanded groups when suggestions change
    useEffect(() => {
        setExpandedGroups({})
    }, [suggestions])

    // Debounced function to fetch suggestions
    const debouncedFetchSuggestionsRef = useRef(
        debounce((data) => {
            if (ADDRESS_SUGGESTIONS_ENABLED) {
                const {value} = data
                if (value.length >= MIN_SUGGESTION_CHARS) {
                    setHasFetched(true)
                    fetchSuggestions({address: value, shouldFetch: true})
                }
            }
        }, ADDRESS_SERVICES_DEBOUNCE_INTERVAL)
    )

    // Effect to handle address1 input changes
    useEffect(() => {
        if (address1.length >= MIN_SUGGESTION_CHARS) {
            debouncedFetchSuggestionsRef.current({value: address1})
        } else {
            setHasFetched(false)
        }
    }, [address1])

    // Sync form value to local state when form is reset or updated externally
    useEffect(() => {
        const formAddress1Value = form.getValues(fields.address1.name)
        if (formAddress1Value && formAddress1Value !== address1) {
            setAddress1(formAddress1Value)
        }
    }, [form.watch(fields.address1.name)])

    useEffect(() => {
        // Focus on the form when the component mounts for accessibility
        // Use preventScroll and blurring to avoid showing the focus ring on programmatic focus
        addressFormRef?.current?.focus({preventScroll: true})
        addressFormRef?.current?.blur()
    }, [])

    useEffect(() => {
        if (showSuggestions && highlightedIndex >= 0 && itemsRef.current[highlightedIndex]) {
            itemsRef.current[highlightedIndex].focus()
        }
    }, [highlightedIndex, showSuggestions])

    const handleAddress1Change = (e) => {
        const value = e.target.value
        setAddress1(value)
        form.setValue(fields.address1.name, value)
        setHasFetched(false)
        setShowSuggestions(true)
    }

    const handleAddress1KeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setShowSuggestions(true)
                setHighlightedIndex(0)
            }
            return
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setHighlightedIndex((prev) =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
                break
            case 'Enter':
                e.preventDefault()
                if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                    handleSuggestionClick(suggestions[highlightedIndex])
                }
                break
            case 'Escape':
                e.preventDefault()
                setShowSuggestions(false)
                setHighlightedIndex(-1)
                break
            default:
                break
        }
    }

    const handleSuggestionItemKeyDown = (e, index) => {
        setHighlightedIndex(index)

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault()
                handleSuggestionClick(suggestions[index])
                break
            case 'Escape':
                e.preventDefault()
                setShowSuggestions(false)
                setHighlightedIndex(-1)
                break
            case 'ArrowDown':
                e.preventDefault()
                if (index < suggestions.length - 1) {
                    setHighlightedIndex(index + 1)
                }
                break
            case 'ArrowUp':
                e.preventDefault()
                if (index > 0) {
                    setHighlightedIndex(index - 1)
                }
                break
            default:
                break
        }
    }

    const handleSuggestionClick = (suggestion) => {
        // Populate fields with the selected suggestion
        setAddress1(suggestion.address1)
        form.setValue(fields.address1.name, suggestion.address1)
        form.setValue(fields.apartment.name, suggestion.address2)
        form.setValue(fields.city.name, suggestion.city)
        form.setValue(fields.stateCode.name, suggestion.stateCode)
        form.setValue(fields.postalCode.name, suggestion.postalCode)
        setShowSuggestions(false)
        if (suggestion.address2) {
            setShowApartment(true)
        }
    }

    // GTM Modification
    const sendGTMEvent = (type) => {
        if (type && !isBillingAddress) {
            sendClickEvent(type)
        }
    }

    return (
        <Stack
            {...styles.formStack}
            aria-label={intl.formatMessage(formTitleAriaLabel)}
            tabIndex="0"
            ref={addressFormRef}
        >
            <Field
                variant="webFloatingField"
                onClick={() => sendGTMEvent('shipping-address_input_first-name')}
                {...fields.firstName}
            />
            <Field
                variant="webFloatingField"
                onClick={() => sendGTMEvent('shipping-address_input_last-name')}
                {...fields.lastName}
            />
            <Box ref={boxRef} position="relative">
                <Field
                    variant="webFloatingField"
                    {...fields.address1}
                    inputProps={{
                        ...fields.address1.inputProps,
                        value: address1,
                        onChange: handleAddress1Change,
                        onKeyDown: handleAddress1KeyDown,
                        onKeyUp: () => setShowSuggestions(true),
                        autoComplete: 'off',
                        ariaAutocomplete: 'list',
                        ariaControls: showSuggestions ? 'address-suggestions' : undefined,
                        ariaExpanded: showSuggestions && suggestions.length > 0,
                        role: 'combobox',
                        maxLength: 50
                    }}
                />
                {showSuggestions &&
                    address1.length >= MIN_SUGGESTION_CHARS &&
                    (hasFetched || isLoading) && (
                        <List
                            id="address-suggestions"
                            ref={suggestionsListRef}
                            role="listbox"
                            {...styles.suggestionList}
                            aria-label={intl.formatMessage({
                                id: 'address.suggestions.aria_label',
                                defaultMessage: 'Address suggestions'
                            })}
                        >
                            {isLoading ? (
                                <ListItem {...styles.suggestionItem} cursor="default" justifyContent="center">
                                    <Spinner size="sm" />
                                </ListItem>
                            ) : suggestions.length > 0 ? (
                                groupedSuggestions.slice(0, ADDRESS_SUGGESTION_LIMIT).map((entry, entryIdx) => {
                                    if (entry.type === 'single') {
                                        const formattedSuggestion = [
                                            entry.suggestion.address1,
                                            entry.suggestion.address2,
                                            entry.suggestion.city,
                                            entry.suggestion.postalCode
                                        ]
                                            .filter(Boolean)
                                            .join(', ')

                                        return (
                                            <ListItem
                                                key={entryIdx}
                                                ref={(el) => (itemsRef.current[entryIdx] = el)}
                                                role="option"
                                                aria-selected={entryIdx === highlightedIndex}
                                                tabIndex={entryIdx === highlightedIndex ? 0 : -1}
                                                onClick={() => handleSuggestionClick(entry.suggestion)}
                                                onKeyDown={(e) => handleSuggestionItemKeyDown(e, entryIdx)}
                                                onMouseEnter={() => setHighlightedIndex(entryIdx)}
                                                {...styles.suggestionItem}
                                            >
                                                {formattedSuggestion}
                                            </ListItem>
                                        )
                                    }

                                    // Grouped (multi-unit) entry
                                    const isExpanded = expandedGroups[entry.key]
                                    return (
                                        <Box key={entry.key}>
                                            <ListItem
                                                {...styles.suggestionItem}
                                                {...styles.suggestionGroupHeader}
                                                onMouseEnter={() => setHighlightedIndex(-1)}
                                                onClick={() =>
                                                    setExpandedGroups((prev) => ({
                                                        ...prev,
                                                        [entry.key]: !prev[entry.key]
                                                    }))
                                                }
                                            >
                                                <Box flex="1">
                                                    <Text {...styles.suggestionGroupLabel}>
                                                        <FormattedMessage
                                                            defaultMessage="Show results for {range}"
                                                            id="address.suggestions.show_results"
                                                            values={{range: entry.rangeLabel}}
                                                        />
                                                    </Text>
                                                    <Text {...styles.suggestionGroupAddress} isTruncated>
                                                        {entry.label}
                                                    </Text>
                                                </Box>
                                                <ChevronDownType2
                                                    sx={{
                                                        width: '14px',
                                                        height: '14px',
                                                        transform: isExpanded
                                                            ? 'rotate(180deg)'
                                                            : 'rotate(0deg)',
                                                        transition: 'transform 0.2s ease-in-out'
                                                    }}
                                                />
                                            </ListItem>
                                            {isExpanded &&
                                                entry.items.map((unit, unitIdx) => (
                                                    <ListItem
                                                        key={unitIdx}
                                                        role="option"
                                                        onClick={() => handleSuggestionClick(unit)}
                                                        {...styles.suggestionItemGroup}
                                                    >
                                                        {unit.address2 || unit.address1}
                                                    </ListItem>
                                                ))}
                                        </Box>
                                    )
                                })
                            ) : (
                                <ListItem {...styles.suggestionItem} cursor="default">
                                    <FormattedMessage
                                        defaultMessage="No address results found."
                                        id="address.suggestions.no_results"
                                    />
                                </ListItem>
                            )}
                        </List>
                    )}
            </Box>
            {showApartment ? (
                <Field
                    variant="webFloatingField"
                    {...fields.apartment}
                />
            ) : (
                <Flex
                    {...styles.apartmentToggle}
                    onClick={() => setShowApartment(true)}
                >
                    <AddIcon w="20px" h="20px" />
                    <Text {...styles.apartmentToggleText}>{intl.formatMessage(addApartmentLabel)}</Text>
                </Flex>
            )}
            <Field
                variant="webFloatingField"
                {...fields.city}
                onClick={() => sendGTMEvent('shipping-address_input_city')}
            />
            <Field
                variant="webFloatingField"
                {...fields.countryCode}
                formControlProps={{isDisabled: true}}
                hideRequiredIndicator
            />
            <SimpleGrid {...styles.fieldRowHalf}>
                <Field
                    variant="webFloatingField"
                    onClick={() => sendGTMEvent('shipping-address_input_state')}
                    {...fields.stateCode}
                    displayValueAsLabel
                />
                <Field
                    variant="webFloatingField"
                    onClick={() => sendGTMEvent('shipping-address_input_zip-code')}
                    {...fields.postalCode}
                />
            </SimpleGrid>
            <Field
                variant="webFloatingField"
                onClick={() => sendGTMEvent('shipping-address_input_telephone')}
                {...fields.phone}
            />
            {customer.isRegistered && !isBillingAddress && (
                <Field
                    labelProps={{textStyle: 'bodyXsRegular'}}
                    {...fields.preferred}
                />
            )}
        </Stack>
    )
}

AddressFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Optional aria label to use for the address form */
    formTitleAriaLabel: MESSAGE_PROPTYPE,

    /** Optional flag to indication if an address is a billing address */
    isBillingAddress: PropTypes.bool
}

export default AddressFields
